/**
 * Rules › Battery and financing › Travel: what a quote charges for the drive from the company's nearest base
 * (docs/features/travel-charge.md). Off by default. Bases are typed as addresses; the server looks each new or
 * changed one up once when the pricing is saved and quotes only measure the distance (no lookup per quote).
 * The section edits the same pricing draft as the battery options (one document, one save).
 */
import { MapPin, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ApiError, errorMessage, type S } from "../api/client";
import { Button, Field, NumberInput, Switch, TextInput } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { MAX_BASES, roadKm, startTravel, travelCharge, travelProblems, withAddress, type Hub, type Travel } from "../lib/travel";

type Pricing = S["PricingSettings"];
/** The live example's system size (kW): accommodation bands depend on it. */
const EXAMPLE_KW = 8;

const row = { alignItems: "end", paddingBottom: 12, borderBottom: "1px solid var(--border)" } as const;

/** The base a refused save names (its address was not found, or could not be looked up), and the reader's words. */
function baseFailure(failure: unknown): { index: number; message: string } | null {
  if (!(failure instanceof ApiError) || !failure.problem.code?.startsWith("travel_base_")) return null;
  const index = (failure.problem.params as { index?: unknown } | undefined)?.index;
  return typeof index === "number" ? { index, message: errorMessage(failure) } : null;
}

export function TravelSection({ pricing, setPricing, footer, failure, Section }: {
  pricing: Pricing;
  setPricing: (p: Pricing) => void;
  footer: ReactNode;
  /** The last save's refusal, shown under the address it names. */
  failure?: unknown;
  Section: (props: { title: string; description?: string; footer?: ReactNode; children: ReactNode }) => ReactNode;
}) {
  const t = useT();
  const intl = useIntlShape();
  const [exampleKm, setExampleKm] = useState<number | null>(100);
  const travel = pricing.travel;
  const on = !!travel && travel.enabled !== false;
  const currency = pricing.currency;
  const money = (v: number) => intl.formatNumber(v, { style: "currency", currency, maximumFractionDigits: v % 1 ? 2 : 0 });
  const set = (patch: Partial<Travel>) => travel && setPricing({ ...pricing, travel: { ...travel, ...patch } });
  const toggle = (value: boolean) =>
    setPricing({ ...pricing, travel: travel ? { ...travel, enabled: value } : startTravel(t("rules.travel.base.defaultName")) });
  const setHub = (i: number, hub: Hub) => travel && set({ hubs: travel.hubs.map((h, j) => (i === j ? hub : h)) });
  const problems = travelProblems(on ? travel : null);
  const refused = baseFailure(failure);

  return (
    <Section title={t("rules.travel.title")} description={t("rules.travel.description")} footer={footer}>
      <div className="stack">
        <Switch checked={on} onChange={toggle} label={t("rules.travel.enabled")} />
        {on && travel && (
          <>
            <div className="stack" style={{ gap: 10 }}>
              <h3>{t("rules.travel.bases.title")}</h3>
              <p className="hint">{t("rules.travel.bases.hint", { max: MAX_BASES })}</p>
              {travel.hubs.map((hub, i) => (
                <div key={i} className="form-grid-3" style={{ ...row, alignItems: "start" }} data-testid="travel-base">
                  <Field label={t("rules.travel.base.name")} error={!hub.name.trim() ? t("rules.travel.base.missing") : undefined}>
                    <TextInput dir="auto" value={hub.name} maxLength={80} onChange={(e) => setHub(i, { ...hub, name: e.target.value })} />
                  </Field>
                  <Field
                    className="span-2-of-3"
                    label={t("rules.travel.base.address")}
                    hint={
                      hub.place && hub.lat != null ? (
                        <span><MapPin size={12} style={{ verticalAlign: -2 }} /> {t("rules.travel.base.found", { place: hub.place })}</span>
                      ) : (hub.address ?? "").trim() ? t("rules.travel.base.pending") : undefined
                    }
                    error={
                      refused?.index === i ? refused.message
                        : !(hub.address ?? "").trim() && (hub.lat == null || hub.lng == null) ? t("rules.travel.base.missing") : undefined
                    }
                  >
                    <TextInput dir="auto" value={hub.address ?? ""} maxLength={300} autoComplete="street-address" onChange={(e) => setHub(i, withAddress(hub, e.target.value))} />
                  </Field>
                  <div>
                    <Button variant="danger" icon={<Trash2 />} disabled={travel.hubs.length === 1} onClick={() => set({ hubs: travel.hubs.filter((_, j) => j !== i) })}>
                      {t("rules.action.remove")}
                    </Button>
                  </div>
                </div>
              ))}
              <div>
                <Button icon={<Plus />} disabled={travel.hubs.length >= MAX_BASES} onClick={() => set({ hubs: [...travel.hubs, { name: "", address: "" }] })}>
                  {t("rules.travel.base.add")}
                </Button>
                {travel.hubs.length >= MAX_BASES && <span className="hint" style={{ marginInlineStart: 8 }}>{t("rules.travel.base.max", { max: MAX_BASES })}</span>}
              </div>
            </div>

            <div className="form-grid-3">
              <Field label={t("rules.travel.perKm")}>
                <NumberInput value={travel.per_km} min={0} step={0.1} suffix={`${currency}/km`} onChange={(v) => set({ per_km: Math.max(0, v ?? 0) })} />
              </Field>
              <Field label={t("rules.travel.freeKm.label")} hint={t("rules.travel.freeKm.hint")}>
                <NumberInput value={travel.free_km} min={0} step={5} suffix="km" onChange={(v) => set({ free_km: Math.max(0, v ?? 0) })} />
              </Field>
              <Field label={t("rules.travel.roadFactor.label")} hint={t("rules.travel.roadFactor.hint")}>
                <NumberInput value={travel.road_factor} min={1} max={3} step={0.05} onChange={(v) => set({ road_factor: Math.min(3, Math.max(1, v ?? 1.25)) })} />
              </Field>
            </div>

            <div className="stack" style={{ gap: 10 }}>
              <h3>{t("rules.travel.bands.title")}</h3>
              <p className="hint">{t("rules.travel.bands.hint")}</p>
              {(travel.bands ?? []).map((band, i, bands) => (
                <div key={i} className="form-grid-3" style={row} data-testid="travel-band">
                  <Field label={t("rules.travel.band.upTo")}>
                    <NumberInput value={band.up_to_km} min={1} step={10} suffix="km" placeholder={t("rules.travel.band.farther")} onChange={(v) => set({ bands: bands.map((b, j) => (i === j ? { ...b, up_to_km: v && v > 0 ? v : null } : b)) })} />
                  </Field>
                  <Field label={t("rules.travel.band.fee")}>
                    <NumberInput value={band.amount} min={0} step={50} suffix={currency} onChange={(v) => set({ bands: bands.map((b, j) => (i === j ? { ...b, amount: Math.max(0, v ?? 0) } : b)) })} />
                  </Field>
                  <div>
                    <Button variant="danger" icon={<Trash2 />} onClick={() => set({ bands: bands.filter((_, j) => j !== i) })}>
                      {t("rules.action.remove")}
                    </Button>
                  </div>
                </div>
              ))}
              {problems.bands && <span className="field-error" role="alert">{t("rules.travel.bands.order")}</span>}
              <div>
                <Button icon={<Plus />} onClick={() => set({ bands: addBand(travel.bands ?? []) })}>
                  {t("rules.travel.band.add")}
                </Button>
              </div>
            </div>

            <div className="form-grid-3">
              <Field label={t("rules.travel.contactBeyond.label")} hint={t("rules.travel.contactBeyond.hint")}>
                <NumberInput value={travel.contact_beyond_km ?? null} min={1} step={10} suffix="km" placeholder={t("rules.travel.noLimit")} onChange={(v) => set({ contact_beyond_km: v && v > 0 ? v : null })} />
              </Field>
            </div>

            <Accommodation travel={travel} set={set} currency={currency} invalid={problems.accommodation} />

            <div className="stack" style={{ gap: 8, padding: 12, borderRadius: "var(--radius-sm)", background: "var(--surface-2)", border: "1px solid var(--border)" }}>
              <div className="form-grid-3">
                <Field label={t("rules.travel.exampleDistance")}>
                  <NumberInput value={exampleKm} min={0} step={10} suffix="km" onChange={setExampleKm} />
                </Field>
              </div>
              <Example travel={travel} km={exampleKm ?? 0} money={money} />
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

/** The bands with one more: 100 km past the farthest limit, kept before an open last band. */
function addBand(bands: S["TravelBand"][]): S["TravelBand"][] {
  const known = bands.map((b) => b.up_to_km).filter((km): km is number => km != null);
  const band = { up_to_km: known.length ? Math.max(...known) + 100 : 50, amount: 0 };
  const last = bands.at(-1);
  return last && last.up_to_km == null ? [...bands.slice(0, -1), band, last] : [...bands, band];
}

function Example({ travel, km, money }: { travel: Travel; km: number; money: (v: number) => string }) {
  const t = useT();
  const intl = useIntlShape();
  const road = roadKm(travel, km);
  const charge = travelCharge(travel, road, EXAMPLE_KW);
  const base = travel.hubs[0]?.name.trim() || t("rules.travel.base.defaultName");
  return (
    <p data-testid="travel-example" role="status" style={{ margin: 0 }}>
      {t("rules.travel.example.text", { km: intl.formatNumber(km), base, road: intl.formatNumber(Math.round(road)), amount: money(charge.travel) })}
      {charge.accommodation != null && <> {t("rules.travel.example.accommodation", { amount: money(charge.accommodation), kw: EXAMPLE_KW })}</>}
      {charge.manual && <> <b>{t("rules.travel.example.manual")}</b></>}
    </p>
  );
}

function Accommodation({ travel, set, currency, invalid }: { travel: Travel; set: (patch: Partial<Travel>) => void; currency: string; invalid: boolean }) {
  const t = useT();
  const rule = travel.accommodation;
  const bands = rule?.bands ?? [];
  const setBands = (next: S["AccommodationBand"][]) => rule && set({ accommodation: { ...rule, bands: next } });
  return (
    <div className="stack" style={{ gap: 10 }}>
      <h3>{t("rules.travel.accommodation.title")}</h3>
      <Switch
        checked={!!rule}
        onChange={(v) => set({ accommodation: v ? { min_km: 150, bands: [{ max_kw: null, amount: 0 }] } : undefined })}
        label={t("rules.travel.accommodation.enabled")}
      />
      {rule && (
        <>
          <div className="form-grid-3">
            <Field label={t("rules.travel.accommodation.minKm")}>
              <NumberInput value={rule.min_km} min={0} step={10} suffix="km" onChange={(v) => set({ accommodation: { ...rule, min_km: Math.max(0, v ?? 0) } })} />
            </Field>
          </div>
          {bands.map((band, i) => (
            <div key={i} className="form-grid-3" style={row} data-testid="travel-accommodation-band">
              <Field label={t("rules.travel.accommodation.upToKw")}>
                <NumberInput value={band.max_kw} min={0.1} step={1} suffix="kW" placeholder={t("rules.travel.accommodation.larger")} onChange={(v) => setBands(bands.map((b, j) => (i === j ? { ...b, max_kw: v && v > 0 ? v : null } : b)))} />
              </Field>
              <Field label={t("rules.travel.accommodation.amount")}>
                <NumberInput value={band.amount} min={0} step={50} suffix={currency} disabled={!!band.contact_for_quote} onChange={(v) => setBands(bands.map((b, j) => (i === j ? { ...b, amount: Math.max(0, v ?? 0) } : b)))} />
              </Field>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <Switch checked={!!band.contact_for_quote} onChange={(v) => setBands(bands.map((b, j) => (i === j ? { ...b, contact_for_quote: v, amount: v ? 0 : b.amount } : b)))} label={t("rules.travel.accommodation.manual")} />
                <Button variant="danger" icon={<Trash2 />} disabled={bands.length === 1} onClick={() => setBands(bands.filter((_, j) => j !== i))}>
                  {t("rules.action.remove")}
                </Button>
              </div>
            </div>
          ))}
          {invalid && <span className="field-error" role="alert">{t("rules.travel.accommodation.order")}</span>}
          <div>
            <Button icon={<Plus />} onClick={() => setBands([{ max_kw: Math.max(1, ...bands.map((b) => b.max_kw ?? 0)) + 4, amount: 0 }, ...bands].sort((a, b) => (a.max_kw ?? Infinity) - (b.max_kw ?? Infinity)))}>
              {t("rules.travel.accommodation.add")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
